import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Grid,
  Avatar,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API_URL } from '../config';
import LandingNavbar from '../components/LandingNavbar';
import Footer from '../components/Footer';
import VoiceChatInterface from '../components/VoiceChatInterface';

// Tipos para as mensagens e respostas do chatbot
interface ChatMessage {
  id?: number;
  content: string;
  isFromUser: boolean;
  messageType: string;
  options?: Option[];
  productSuggestion?: ProductSuggestion;
  timestamp?: Date;
}

interface Option {
  value: string;
  label: string;
}

interface ProductSuggestion {
  id: number;
  titulo: string;
  descricao: string;
  valor: number;
  prazo: number;
  parcela: number;
  taxa: number;
}

interface ChatbotResponse {
  message: string;
  session_key: string;
  message_type: string;
  options?: Option[];
  product_suggestion?: ProductSuggestion;
  lead_created?: boolean;
  intent_score?: number;
}

interface ChatbotPageProps {
  isWidget?: boolean;
  onClose?: () => void;
}

// Componentes estilizados
const ChatContainer = styled(Paper)(({ theme }) => ({
  height: 'calc(100vh - 200px)',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  backgroundColor: '#f5f5f5',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    height: 'calc(100vh - 160px)',
  },
}));

const MessageList = styled(List)(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#a8a8a8',
  },
}));

const UserMessage = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  borderRadius: '22px 22px 0 22px',
  maxWidth: '85%',
  wordBreak: 'break-word',
  fontSize: '1.1rem',
  lineHeight: 1.5,
  boxShadow: '0 3px 10px rgba(0, 0, 0, 0.15)',
}));

const BotMessage = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: '#fff',
  borderRadius: '22px 22px 22px 0',
  maxWidth: '85%',
  wordBreak: 'break-word',
  boxShadow: '0 3px 12px rgba(0, 0, 0, 0.1)',
  fontSize: '1.1rem',
  lineHeight: 1.5,
  border: '1px solid #f0f0f0',
}));

const OptionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: '20px',
  textTransform: 'none',
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
}));

const ProductCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  borderRadius: '12px',
  boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-5px)',
  },
}));

const BotAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  width: 42,
  height: 42,
  fontSize: '1.1rem',
  fontWeight: 'bold',
}));

const ChatbotPage: React.FC<ChatbotPageProps> = ({ isWidget = false, onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [leadInfo, setLeadInfo] = useState<any>(null);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voiceConfig, setVoiceConfig] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Efeito para rolar para a mensagem mais recente
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'end'
      });
    }, 100);
  };

  // Carregar configurações de voz
  const loadVoiceConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/chatbot/prompt-configs`);
      const configs = response.data;
      
      // Buscar a configuração ativa
      const activeConfig = configs.find((config: any) => config.is_active);
      
      if (activeConfig && activeConfig.voice_enabled) {
        setVoiceEnabled(true);
        setVoiceConfig({
          voice_type: activeConfig.voice_type || 'alloy',
          voice_speed: activeConfig.voice_speed || 1.0,
          voice_model: activeConfig.voice_model || 'tts-1',
          auto_voice_response: activeConfig.auto_voice_response || false
        });
      }
    } catch (error) {
      console.error('Erro ao carregar configurações de voz:', error);
    }
  };

  // Inicializar o chatbot
  const initChatbot = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.post<ChatbotResponse>(`${API_URL}/chatbot/message`, {
        message: '',
        session_key: null,
        lead_id: leadInfo?.id || null,
        user_info: null
      });

      setSessionKey(response.data.session_key);
      
      // Adicionar mensagem de boas-vindas
      setMessages([{
        content: response.data.message,
        isFromUser: false,
        messageType: response.data.message_type,
        options: response.data.options,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Erro ao inicializar o chatbot:', error);
      // Adicionar mensagem de erro
      setMessages([{
        content: 'Desculpe, estamos com problemas para conectar ao chatbot. Por favor, tente novamente mais tarde.',
        isFromUser: false,
        messageType: 'text',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [leadInfo]);

  // Efeito para iniciar o chatbot quando a página carrega
  useEffect(() => {
    initChatbot();
    loadVoiceConfig();

    // Extrair informações do lead da URL se disponíveis
    const queryParams = new URLSearchParams(location.search);
    const leadId = queryParams.get('leadId');
    const source = queryParams.get('source');

    if (leadId) {
      setLeadInfo({ id: leadId, source });
    }

    // Prevenir scroll da página quando o usuário interage com o chat
    const preventPageScroll = (e: Event) => {
      e.stopPropagation();
    };

    const chatContainer = document.querySelector('.chat-container');
    if (chatContainer) {
      chatContainer.addEventListener('wheel', preventPageScroll, { passive: false });
      chatContainer.addEventListener('touchmove', preventPageScroll, { passive: false });
    }

    return () => {
      if (chatContainer) {
        chatContainer.removeEventListener('wheel', preventPageScroll);
        chatContainer.removeEventListener('touchmove', preventPageScroll);
      }
    };
  }, [location, initChatbot]);

  // Enviar mensagem para o chatbot
  const sendMessage = async (message: string) => {
    if (!message.trim() && !sessionKey) return;

    // Adicionar mensagem do usuário à lista
    const userMessage: ChatMessage = {
      content: message,
      isFromUser: true,
      messageType: 'text',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Usar o novo endpoint de captura de lead
      const response = await axios.post<ChatbotResponse>(`${API_URL}/chatbot/lead-capture`, {
        message,
        session_key: sessionKey,
        lead_id: leadInfo?.id || null,
        user_info: null
      });

      // Adicionar resposta do bot à lista
      const botMessage: ChatMessage = {
        content: response.data.message,
        isFromUser: false,
        messageType: response.data.message_type,
        options: response.data.options,
        productSuggestion: response.data.product_suggestion,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      
      // Se um lead foi criado, mostrar feedback
      if (response.data.lead_created) {
        console.log('Lead capturado com sucesso! Pontuação de intenção:', response.data.intent_score);
      }
      
      // Se a resposta automática por voz estiver habilitada, reproduzir o áudio
      if (voiceConfig?.auto_voice_response && voiceEnabled) {
        handleTextToSpeech(response.data.message);
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      // Adicionar mensagem de erro
      setMessages(prev => [...prev, {
        content: 'Desculpe, ocorreu um erro ao processar sua mensagem. Por favor, tente novamente.',
        isFromUser: false,
        messageType: 'text',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Lidar com o envio do formulário
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (inputValue.trim()) {
      sendMessage(inputValue);
    }
  };

  // Lidar com o clique em uma opção
  const handleOptionClick = (option: Option) => {
    sendMessage(option.label);
  };

  // Função para converter texto em fala
  const handleTextToSpeech = async (text: string) => {
    if (!voiceConfig) return;
    
    try {
      const response = await axios.post(`${API_URL}/text-to-speech`, {
        text,
        voice: voiceConfig.voice_type,
        speed: voiceConfig.voice_speed,
        model: voiceConfig.voice_model
      }, {
        responseType: 'blob'
      });
      
      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.play().catch(error => {
        console.error('Erro ao reproduzir áudio:', error);
      });
      
      // Limpar URL após reprodução
      audio.addEventListener('ended', () => {
        URL.revokeObjectURL(audioUrl);
      });
    } catch (error) {
      console.error('Erro ao converter texto em fala:', error);
    }
  };

  // Função para processar transcrição de voz
  const handleVoiceTranscription = (transcription: string) => {
    setInputValue(transcription);
  };

  // Renderizar uma mensagem individual
  const renderMessage = (message: ChatMessage, index: number) => {
    const isUser = message.isFromUser;
    
    return (
      <ListItem 
        key={index} 
        sx={{ 
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          alignItems: 'flex-start',
          mb: 2.5,
          px: 0,
        }}
        component={motion.li}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {!isUser && (
          <BotAvatar sx={{ mr: 1.5, mt: 0.5 }}>
            MP
          </BotAvatar>
        )}
        <Box>
          {isUser ? (
            <UserMessage>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 500, lineHeight: 1.5 }}>
                {message.content}
              </Typography>
            </UserMessage>
          ) : (
            <React.Fragment>
              <BotMessage>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 400, lineHeight: 1.5 }}>
                  {message.content}
                </Typography>
              </BotMessage>
              
              {/* Renderizar opções de botões se disponíveis */}
              {message.options && message.options.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {message.options.map((option, optIndex) => (
                    <OptionButton
                      key={optIndex}
                      variant="outlined"
                      color="primary"
                      size="medium"
                      onClick={() => handleOptionClick(option)}
                      sx={{ fontSize: '1rem', py: 1, px: 2.5 }}
                    >
                      {option.label}
                    </OptionButton>
                  ))}
                </Box>
              )}
              
              {/* Renderizar sugestão de produto se disponível */}
              {message.productSuggestion && (
                <motion.div whileHover={{ scale: 1.02 }}>
                  <ProductCard>
                    <CardContent>
                      <Typography variant="h6" color="primary" gutterBottom>
                        {message.productSuggestion.titulo}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {message.productSuggestion.descricao}
                      </Typography>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Valor:</strong> R$ {message.productSuggestion.valor.toLocaleString('pt-BR')}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Prazo:</strong> {message.productSuggestion.prazo} meses
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Parcela:</strong> R$ {message.productSuggestion.parcela.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Taxa:</strong> {message.productSuggestion.taxa}%
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions>
                      <Button size="small" color="primary" onClick={() => sendMessage('Quero saber mais sobre este plano')}>Saber mais</Button>
                      <Button size="small" color="secondary" onClick={() => sendMessage('Quero falar com um consultor')}>Falar com consultor</Button>
                    </CardActions>
                  </ProductCard>
                </motion.div>
              )}
            </React.Fragment>
          )}
          
          {/* Timestamp da mensagem */}
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              display: 'block', 
              textAlign: isUser ? 'right' : 'left',
              mt: 0.5,
              fontSize: '0.75rem',
              opacity: 0.7
            }}
          >
            {message.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>
      </ListItem>
    );
  };

  return (
    <Box sx={{ minHeight: isWidget ? 'unset' : '100vh', display: 'flex', flexDirection: 'column' }}>
      {!isWidget && <LandingNavbar />}
      
      <Container maxWidth="md" sx={{ flexGrow: 1, py: 4 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          {!isWidget && (
            <IconButton 
              color="primary" 
              onClick={() => navigate('/')} 
              sx={{ mr: 1 }}
              aria-label="Voltar para a página inicial"
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          {isWidget && onClose && (
            <IconButton 
              color="primary" 
              onClick={onClose} 
              sx={{ mr: 1 }}
              aria-label="Fechar chatbot"
            >
              <CloseIcon />
            </IconButton>
          )}
          <Typography variant="h5" component="h1">
            Assistente Virtual MedPrime
          </Typography>
        </Box>
        
        <ChatContainer className="chat-container">
          {isLoading && messages.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>Iniciando conversa...</Typography>
            </Box>
          ) : (
            <>
              <MessageList>
                {messages.map(renderMessage)}
                <div ref={messagesEndRef} />
              </MessageList>
              
              <Box component="form" onSubmit={handleSubmit} sx={{ 
                display: 'flex', 
                gap: 1, 
                padding: 2, 
                backgroundColor: '#fff', 
                borderTop: '1px solid #e0e0e0', 
                borderRadius: '0 0 12px 12px',
                alignItems: 'center',
                minHeight: '80px'
              }}>
                <TextField
                  fullWidth
                  placeholder="Digite sua mensagem..."
                  variant="outlined"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  disabled={isLoading}
                  size="medium"
                  multiline
                  maxRows={3}
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '25px',
                      fontSize: '1.1rem',
                      minHeight: '48px',
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#bdbdbd',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
                
                {/* Componente de Interface de Voz */}
                {voiceEnabled && (
                  <VoiceChatInterface
                    onTranscription={handleVoiceTranscription}
                    onTextToSpeech={handleTextToSpeech}
                    voiceConfig={voiceConfig}
                    disabled={isLoading}
                  />
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isLoading || !inputValue.trim()}
                  endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                  sx={{ 
                    borderRadius: '50%', 
                    minWidth: '48px', 
                    height: '48px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    }
                  }}
                >
                  {isMobile ? '' : 'Enviar'}
                </Button>
              </Box>
            </>
          )}
        </ChatContainer>
      </Container>
      
      {!isWidget && <Footer />}
    </Box>
  );
};

export default ChatbotPage;